import Hls from 'hls.js'
import { useEffect, useRef, useState } from 'react'

import type { PlaybackStats } from '../lib/api'
import { useCinemaMode } from '../lib/useCinemaMode'

type Props = {
  src: string | null
  muted: boolean
  onStats?: (stats: PlaybackStats | null) => void
}

export function Player({ src, muted, onStats }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const { cinema, available: cinemaAvailable, toggle: toggleCinema } = useCinemaMode()

  // Entering theatre mode with the player half off-screen is jarring.
  useEffect(() => {
    if (cinema) shellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [cinema])

  // Held in a ref so changing the callback never restarts playback.
  const onStatsRef = useRef(onStats)
  onStatsRef.current = onStats

  useEffect(() => {
    const video = videoRef.current
    if (!video || !src) {
      onStatsRef.current?.(null)
      return
    }

    setError(null)
    let hls: Hls | null = null
    let stalls = 0

    const countStall = () => {
      stalls += 1
    }
    video.addEventListener('waiting', countStall)

    // Safari and iOS play HLS natively and do it better than MSE ever will.
    if (!Hls.isSupported()) {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src
        void video.play().catch(() => {})
      } else {
        setError('Aquest navegador no pot reproduir HLS')
      }
    } else {
      hls = new Hls({
        lowLatencyMode: true,
        liveSyncDurationCount: 3,
        // The stream is live-only; holding old segments just wastes memory.
        backBufferLength: 30,
        manifestLoadingMaxRetry: 8,
        manifestLoadingRetryDelay: 1000,
        levelLoadingMaxRetry: 8,
        fragLoadingMaxRetry: 8,
      })

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || !hls) return
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // The playlist often 404s for a moment while ffmpeg writes segment 1.
            hls.startLoad()
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError()
            break
          default:
            setError(`Error de reproducció: ${data.details}`)
            hls.destroy()
            hls = null
        }
      })

      hls.loadSource(src)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, () => void video.play().catch(() => {}))
    }

    const sampler = setInterval(() => {
      const report = onStatsRef.current
      if (!report) return

      let bufferSeconds = 0
      for (let i = 0; i < video.buffered.length; i += 1) {
        const end = video.buffered.end(i)
        const start = video.buffered.start(i)
        if (video.currentTime >= start - 0.5 && video.currentTime <= end) {
          bufferSeconds = end - video.currentTime
        }
      }

      const quality =
        typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : null

      report({
        bufferSeconds: Math.max(0, bufferSeconds),
        droppedFrames: quality?.droppedVideoFrames ?? 0,
        totalFrames: quality?.totalVideoFrames ?? 0,
        bandwidthEstimate: hls?.bandwidthEstimate ?? 0,
        stalls,
        playing: !video.paused && !video.ended && video.readyState > 2,
      })
    }, 1000)

    return () => {
      clearInterval(sampler)
      video.removeEventListener('waiting', countStall)
      onStatsRef.current?.(null)
      if (hls) {
        hls.destroy()
      } else {
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [src])

  return (
    <div ref={shellRef} className={`player${cinema ? ' player--cinema' : ''}`}>
      <video ref={videoRef} controls playsInline muted={muted} poster="" />
      {!src && <div className="player__placeholder">Enganxa un enllaç AceStream per començar</div>}
      {error && <div className="player__error">{error}</div>}
      {cinemaAvailable && (
        <button
          type="button"
          className="player__cinema"
          onClick={toggleCinema}
          aria-pressed={cinema}
          title={cinema ? 'Surt del mode cinema (T)' : 'Mode cinema (T)'}
          aria-label={cinema ? 'Surt del mode cinema' : 'Mode cinema'}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            {cinema ? (
              <path
                d="M4 7h16v10H4z M8 10h8v4H8z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="M3 6h18v12H3z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </button>
      )}
    </div>
  )
}
